import { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { t, useT } from '@/i18n';
import { appServer, appShell, normalizeServer, setAppServer } from '@/lib/app';
import type { ThemeChoice } from '@/lib/storage';
import { useLatest } from '@/lib/useLatest';
import { useTheme } from '@/theme/useTheme';

import { Button } from './Button';
import { HowLink } from './HowBridgeWorks';
import { GearIcon, ShieldCheckIcon } from './Icons';
import { LanguageList } from './LanguagePicker';
import styles from './Layout.module.css';
import { Segmented } from './LinkOptions';
import { QuickSendSettings } from './QuickSendSettings';
import { WindowPrivacySettings } from './WindowPrivacySettings';
import ui from './ui.module.css';

/** The Mac's Settings… menu item (⌘,) sends this to the page (apps/apple/Bridge/WebController.swift). */
export const OPEN_SETTINGS_EVENT = 'bridge:settings';

const themes = (): readonly { readonly value: ThemeChoice; readonly label: string }[] => [
  { value: 'auto', label: t('theme.system') },
  { value: 'light', label: t('theme.light') },
  { value: 'dark', label: t('theme.dark') },
];

/** `bridge.example.com` for `https://bridge.example.com`; a port or a plain `http:` stays visible. */
function label(origin: string): string {
  return origin.startsWith('https://') ? origin.slice('https://'.length) : origin;
}

/**
 * The native app's settings, where apps keep them: a Settings row — at the foot of the sidebar in a wide window, at
 * the foot of the first screen on a phone, a tablet or a narrow window; on the Mac also Settings… (⌘,). One sheet
 * with the appearance and the server the app talks to. Bridge is open source,
 * so a company can run its own server and point the app at it; the choice is
 * kept on this device, and changing it reloads the app (the API client is
 * built once).
 */
export function AppSettings(): React.JSX.Element | null {
  useT();
  const shell = appShell();
  const [open, setOpen] = useState(false);
  // the appearance is applied by this hook; on the site the footer's switch holds it, in the app this does
  useTheme();
  useEffect(() => {
    const show = (): void => {
      setOpen(true);
    };
    window.addEventListener(OPEN_SETTINGS_EVENT, show);
    return () => {
      window.removeEventListener(OPEN_SETTINGS_EVENT, show);
    };
  }, []);
  const location = useLocation();
  if (shell === null) return null;
  const home = location.pathname === '/' && !new URLSearchParams(location.search).has('do');
  const openSettings = (): void => {
    setOpen(true);
  };
  return (
    <>
      {/* a Settings row: at the foot of the sidebar in a wide window, as Mac apps have it; under the first screen otherwise */}
      {/* on a phone the row also holds “Why it's safe”, on the right, in the same quiet grey */}
      <div className={styles.appFoot} data-home={home}>
        <button type="button" className={styles.appSidebarSettings} data-home={home} aria-haspopup="dialog" onClick={openSettings} data-testid="app-settings-row">
          <GearIcon size={16} />
          {t('settings.title')}
        </button>
        {home ? (
          <HowLink className={styles.appFootHow} testId="app-how-row">
            <ShieldCheckIcon size={20} />
            {t('settings.whySafe')}
          </HowLink>
        ) : null}
      </div>
      {open ? (
        <SettingsDialog
          shipped={shell.server}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function SettingsDialog({ shipped, onClose }: { readonly shipped: string; readonly onClose: () => void }): React.JSX.Element {
  useT();
  const id = useId();
  const first = useRef<HTMLDivElement>(null);
  const { choice, set } = useTheme();
  const current = appServer() ?? shipped;
  const [value, setValue] = useState(label(current));
  const [error, setError] = useState<string | null>(null);

  // the focus goes to the first control once, when the window opens, and stays where the person put it after a re-render
  // (a new language redraws the window)
  const close = useLatest(onClose);
  useEffect(() => {
    first.current?.querySelector<HTMLButtonElement>('[role="radio"][aria-checked="true"]')?.focus();
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') close.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [close]);

  const apply = (origin: string | null): void => {
    setAppServer(origin);
    window.location.reload();
  };

  /** Done: a changed server is saved (and the app reloads); otherwise the sheet just closes. */
  const done = (): void => {
    const origin = normalizeServer(value);
    if (origin === null) {
      setError(t('settings.serverInvalid'));
      return;
    }
    if (origin === current) {
      onClose();
      return;
    }
    apply(origin === shipped ? null : origin);
  };

  return (
    <div
      className={ui.backdrop}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        className={[ui.modal, styles.settings].join(' ')}
        onSubmit={(event) => {
          event.preventDefault();
          done();
        }}
        noValidate
        data-testid="settings-dialog"
      >
        {/* one scroll area: the title scrolls away with the content (the buttons below stay) */}
        <div className={ui.modalBody} tabIndex={0}>
        <h2 id={`${id}-title`} className={ui.modalTitle}>
          {t('settings.title')}
        </h2>
        <div ref={first} className={styles.settingsGroup}>
          <Segmented label={t('settings.appearance')} options={themes()} value={choice} onChange={set} />
        </div>
        <LanguageList />
        <QuickSendSettings />
        <WindowPrivacySettings />
        <div className={styles.settingsGroup}>
          <label className={ui.fieldLabel} htmlFor={`${id}-server`}>
            {t('settings.server')}
          </label>
          <input
            id={`${id}-server`}
            className={[ui.textField, error === null ? '' : ui.textFieldInvalid].join(' ')}
            type="url"
            inputMode="url"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              setError(null);
            }}
            aria-invalid={error !== null}
            aria-describedby={`${id}-hint`}
            placeholder={label(shipped)}
          />
          {error === null ? (
            <p id={`${id}-hint`} className={ui.fieldHint}>
              {t('settings.serverHint')}{' '}
              {current === shipped ? null : (
                <button
                  type="button"
                  className={styles.settingsReset}
                  onClick={() => {
                    apply(null);
                  }}
                >
                  {t('settings.serverReset', { server: label(shipped) })}
                </button>
              )}
            </p>
          ) : (
            <p id={`${id}-hint`} className={ui.fieldError} role="alert">
              {error}
            </p>
          )}
        </div>
        </div>
        <div className={ui.modalActions}>
          <Button type="submit" variant="primary" size="small">
            {t('common.done')}
          </Button>
        </div>
      </form>
    </div>
  );
}
